import type { ReasonNode } from '../core/types'
import { retractedWorks } from '../core/types'
import { TAU } from '../core/geometry'

/**
 * The mark for a claim whose source check cites a withdrawn work: the check's
 * own rim, broken open.
 *
 * The card, the strip, the path, the tree, the briefing and the coach all said
 * "cites a retracted work" (D214); the canvas drew the claim exactly as it drew
 * any other checked claim. The mark is built from the one that is already
 * there. A claim checked against sources wears a crisp contour at its disc's
 * edge — "somebody looked", the quietest mark on the canvas — and this is that
 * contour lifted clear of the disc and broken open on the node's own spoke: the
 * look still happened, and part of what it read has been taken back. No new hue and
 * no new glyph: it is not a reading and not a verdict on the claim, only a
 * publisher withdrawing a record, and a mark louder than the check it qualifies
 * would say otherwise. Static, so reduced motion draws it unchanged, and inked
 * the way the contour is, so each theme's physics carry it.
 *
 * Not the closed path's mark, which the legend calls "a ring with a bar across
 * it" and the renderers' comments a "retraction bulb" — the neuroanatomist's
 * word for a degenerating process, which is about the path and names no paper.
 * Here and not in `core/`, because both renderers stroke it and nothing else
 * does; the rule for WHEN is `retractedWorks`, the one the card and the coach
 * read.
 */

/** Where the broken rim stands, in disc radii: clear of a filled disc's edge,
 *  and of a hollow ring's stroke (which reaches 1.33r), so the opening reads
 *  against the ground rather than against the node's own ink. */
export const RIM_OPEN_R = { filled: 1.5, hollow: 1.62 } as const
/** Half the opening, in radians: about 90° open, wide enough to read as a break
 *  at a six-pixel node. */
export const RIM_OPEN_HALF = 0.78
/** Where the opening faces, from the node's outward spoke: straight back along
 *  it, toward the premise. The outward side is taken — the caption stands on the
 *  spoke 20px out, and its halo swallowed an opening there — and the inward side
 *  is the one place on a node no caption goes first. The strand that arrives
 *  there has tapered to its thinnest, so the break still reads as a break. On
 *  the spoke, so it holds still as siblings land. */
export const RIM_OPEN_AT = Math.PI

/**
 * Whether the live check on this claim cites a withdrawn work. Asked per node per
 * frame, so the cheap half goes first: almost no check carries a notice at all,
 * and `retractedWorks` hashes the claim to know the check is still live.
 */
export function citesRetracted(n: ReasonNode): boolean {
  const cites = n.verification?.corpus?.cites
  if (!cites?.some((c) => c.notice === 'retracted')) return false
  return retractedWorks(n).length > 0
}

/** The broken rim's path for a node whose outward spoke points at `spoke`; the
 *  caller strokes it. */
export function traceBrokenRim(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  hollow: boolean,
  spoke: number,
) {
  const rr = r * (hollow ? RIM_OPEN_R.hollow : RIM_OPEN_R.filled)
  const open = spoke + RIM_OPEN_AT
  ctx.beginPath()
  ctx.arc(x, y, rr, open + RIM_OPEN_HALF, open - RIM_OPEN_HALF + TAU)
}
